import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, Video, Trash2, Loader2, Paperclip } from "lucide-react";

/**
 * Per-issue photo/video attachments for a Job Card "Noticed Issue".
 * Reuses the job-card disk-upload pattern via dedicated backend endpoints:
 *   GET    /jobcards/:jc/issues/:issue/media
 *   POST   /jobcards/:jc/issues/:issue/upload-media
 *   GET    /jobcards/:jc/issues/:issue/media/:index
 *   DELETE /jobcards/:jc/issues/:issue/media/:index
 *
 * Props:
 *   jobCardId, issueId  - identifiers
 *   readOnly            - hide upload/delete controls (View screen)
 */
const IssueMediaAttachments = ({ jobCardId, issueId, readOnly = false }) => {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const canFetch = Boolean(jobCardId && issueId);

  const fetchMedia = useCallback(async () => {
    if (!canFetch) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/jobcards/${jobCardId}/issues/${issueId}/media`);
      setMedia(res.data?.media || []);
    } catch (e) {
      setMedia([]);
    } finally {
      setLoading(false);
    }
  }, [API, jobCardId, issueId, canFetch]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));
      await axios.post(
        `${API}/jobcards/${jobCardId}/issues/${issueId}/upload-media`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${files.length} file(s) attached`);
      await fetchMedia();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to attach media";
      toast.error(detail);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async (index) => {
    try {
      await axios.delete(`${API}/jobcards/${jobCardId}/issues/${issueId}/media/${index}`);
      toast.success("Attachment removed");
      await fetchMedia();
    } catch (e) {
      toast.error("Failed to remove attachment");
    }
  };

  const mediaUrl = (index) => `${API}/jobcards/${jobCardId}/issues/${issueId}/media/${index}`;

  if (readOnly && media.length === 0) return null;

  return (
    <div className="mt-2" data-testid={`issue-media-${issueId}`}>
      {!readOnly && (
        <div className="flex items-center gap-2 mb-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleUpload}
            className="hidden"
            id={`issue-media-input-${issueId}`}
            disabled={uploading}
          />
          <label htmlFor={`issue-media-input-${issueId}`}>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="cursor-pointer border-blue-300 text-blue-700 hover:bg-blue-50"
              disabled={uploading}
              onClick={() => inputRef.current && inputRef.current.click()}
              data-testid={`issue-media-attach-btn-${issueId}`}
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Paperclip className="h-3.5 w-3.5 mr-1.5" />
              )}
              {uploading ? "Uploading..." : "Attach Photos / Videos"}
            </Button>
          </label>
          {media.length > 0 && (
            <span className="text-xs text-gray-500">{media.length} attached</span>
          )}
        </div>
      )}

      {media.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid={`issue-media-list-${issueId}`}>
          {media.map((m, index) => (
            <div
              key={`${m.filename}-${index}`}
              className="relative group rounded-lg overflow-hidden border bg-gray-50"
              data-testid={`issue-media-item-${issueId}-${index}`}
            >
              {m.type === "video" ? (
                <video
                  src={mediaUrl(index)}
                  controls
                  className="w-full h-24 object-cover bg-black"
                />
              ) : (
                <a href={mediaUrl(index)} target="_blank" rel="noopener noreferrer">
                  <img
                    src={mediaUrl(index)}
                    alt={m.filename}
                    className="w-full h-24 object-cover"
                    loading="lazy"
                  />
                </a>
              )}
              <div className="absolute top-1 left-1 bg-black/60 text-white rounded px-1 py-0.5 flex items-center">
                {m.type === "video" ? <Video className="h-3 w-3" /> : <Camera className="h-3 w-3" />}
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => handleDelete(index)}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`issue-media-delete-${issueId}-${index}`}
                  title="Remove"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IssueMediaAttachments;
